/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000CheckBox from '../../../../u2000Core/components/formControls/checkbox/U2000CheckBox';
import U2000ComboBox from '../../../../u2000Core/components/formControls/combobox/U2000ComboBox';
import { U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../../U3206';
import { useU3206Context } from '../../../U3206Context';
import type {
  U3206AiUserGroupFormRow,
  U3206DataAccessProfile,
  U3206DataAccessRow,
} from '../../../U3206Service';
import {
  u3206BuildDataAccessRowsFromProfile,
  u3206ComputeDataAccessPreview,
  u3206EnsureDataAccessRows,
  u3206GroupDomainsByTheme,
  u3206IsDataAccessCustomized,
  u3206ResolveDomainDescription,
  u3206ResolveDomainLabel,
  type U3206PresetDataAccessProfile,
} from '../../../utils/u3206DataAccessHelpers';
import { useU3206CopyDataAccessHandlers } from '../../../hooks/useU3206CopyDataAccessHandlers';
import U3206DataAccessPreviewPanel from './U3206DataAccessPreviewPanel';

interface U3206DataAccessTabProps {
  formStateHandlers: U2000FormStateHandlers<U3206AiUserGroupFormRow>;
  t: TFunction;
}

type U3206ProfileOption = {
  code: U3206DataAccessProfile;
  label: string;
};

type U3206CopySourceOption = {
  id: string;
  label: string;
};

/** Construit un objet imbriqué pour U2000CheckBox (fieldName avec points = chemin). */
function u3206SetNestedBoolean(path: string, value: boolean): Record<string, unknown> {
  const parts = path.split('.');
  const root: Record<string, unknown> = {};
  let cursor: Record<string, unknown> = root;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const next: Record<string, unknown> = {};
    cursor[parts[i]] = next;
    cursor = next;
  }
  cursor[parts[parts.length - 1]] = value;
  return root;
}

function u3206ResolveProfileLabel(
  code: U3206DataAccessProfile,
  profiles: { code: U3206DataAccessProfile; labelFr: string; labelEn: string }[],
  locale: string,
): string {
  const match = profiles.find((profile) => profile.code === code);
  if (!match) return code;
  return locale.startsWith('fr') ? match.labelFr : match.labelEn;
}

function U3206DataAccessTab({ formStateHandlers, t }: U3206DataAccessTabProps) {
  const { i18n } = useTranslation(NS);
  const { initLU, gridStateHandlers } = useU3206Context();
  const { copying, copyDataAccessFromGroup } = useU3206CopyDataAccessHandlers();
  const [copySourceId, setCopySourceId] = useState('');
  const data = formStateHandlers.getData();
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const groupId = data?.id?.trim() ?? '';
  const domains = initLU.dataDomains ?? [];
  const profiles = initLU.dataAccessProfiles ?? [];

  const effectiveRows = useMemo(
    () => u3206EnsureDataAccessRows(data?.dataAccess ?? [], domains),
    [data?.dataAccess, domains],
  );

  const profile = data?.dataAccessProfile ?? 'minimal';
  const showCustomBadge = u3206IsDataAccessCustomized(profile, effectiveRows, domains);
  const preview = useMemo(
    () => u3206ComputeDataAccessPreview(domains, effectiveRows),
    [domains, effectiveRows],
  );
  const themes = useMemo(() => u3206GroupDomainsByTheme(domains), [domains]);

  const profileOptions = useMemo<U3206ProfileOption[]>(
    () =>
      profiles.map((item) => ({
        code: item.code,
        label: u3206ResolveProfileLabel(item.code, profiles, i18n.language),
      })),
    [i18n.language, profiles],
  );

  const otherGroups = gridStateHandlers
    .getData()
    .filter((row) => row.id && row.id !== groupId);

  const copySourceOptions = useMemo<U3206CopySourceOption[]>(
    () =>
      otherGroups.map((group) => ({
        id: group.id,
        label: `${group.code} — ${group.name}`,
      })),
    [otherGroups],
  );

  const applyDataAccessState = (nextProfile: U3206DataAccessProfile, nextRows: U3206DataAccessRow[]) => {
    const current = formStateHandlers.getData();
    if (!current) return;
    formStateHandlers.setData({
      ...current,
      dataAccessProfile: nextProfile,
      dataAccess: nextRows,
    });
  };

  const handleProfileChange = (nextProfile: U3206DataAccessProfile) => {
    if (readonly) return;
    if (nextProfile === 'custom') {
      applyDataAccessState('custom', effectiveRows);
      return;
    }
    const preset = nextProfile as U3206PresetDataAccessProfile;
    applyDataAccessState(preset, u3206BuildDataAccessRowsFromProfile(preset, domains));
  };

  const toggleDomain = (domainCode: string, allowed: boolean) => {
    if (readonly) return;
    const nextRows = effectiveRows.map((row) =>
      row.dataDomainCode === domainCode ? { ...row, allowed } : row,
    );
    applyDataAccessState('custom', nextRows);
  };

  const handleCopy = async () => {
    if (readonly || !groupId || !copySourceId) return;
    const normalized = await copyDataAccessFromGroup(groupId, copySourceId);
    if (normalized) {
      formStateHandlers.setData(normalized);
      setCopySourceId('');
    }
  };

  const profileFormHandlers: U2000FormStateHandlers<U3206AiUserGroupFormRow> = {
    ...formStateHandlers,
    onChange: (e: ChangeEvent<HTMLInputElement>, value: unknown) => {
      if (e.target.id === 'dataAccessProfile') {
        if (value == null || value === '') return;
        handleProfileChange(value as U3206DataAccessProfile);
        return;
      }
      formStateHandlers.onChange?.(e, value as any);
    },
  };

  const copyFormHandlers: U2000FormStateHandlers<{ copyDataAccessSource: string }> = {
    ...(formStateHandlers as unknown as U2000FormStateHandlers<{ copyDataAccessSource: string }>),
    getData: () => ({ copyDataAccessSource: copySourceId }),
    onChange: (_e, value) => {
      setCopySourceId(typeof value === 'string' ? value : '');
    },
  };

  const domainCheckHandlers = (domainCode: string, allowed: boolean): U2000FormStateHandlers<any> => ({
    ...formStateHandlers,
    getData: () => u3206SetNestedBoolean(domainCode, allowed),
    onChange: (_e, value) => {
      toggleDomain(domainCode, value === true);
    },
  });

  if (!groupId) {
    return (
      <p className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
        {t('dataAccessSaveFirst', { ns: NS })}
      </p>
    );
  }

  if (domains.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('dataAccessNoDomains', { ns: NS })}</p>;
  }

  const allowedByCode = new Map(effectiveRows.map((row) => [row.dataDomainCode, row.allowed]));

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
      <div className="space-y-3 lg:col-span-2">
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          {t('dataAccessResidualRiskHelp', { ns: NS })}
        </p>
      </div>

      <div className="space-y-6">
        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="flex flex-wrap items-center gap-3">
            {showCustomBadge && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                {t('dataAccessCustomBadge', { ns: NS })}
              </span>
            )}
          </div>
          <div className="max-w-md">
            <U2000ComboBox<U3206ProfileOption>
              formStateHandlers={profileFormHandlers as any}
              fieldName="dataAccessProfile"
              label={t('dataAccessProfile', { ns: NS })}
              data={profileOptions}
              keyField="code"
              descField="label"
              readonly={readonly}
              getValue={(item) => item.code}
              findItemByValue={(value, items) => items.find((item) => item.code === value) ?? null}
              formatLabel={(item) => item.label}
            />
          </div>
        </div>

        <div className="space-y-3">
          {[...themes.entries()].map(([themeCode, themeDomains]) => (
            <details key={themeCode} open className="rounded-lg border border-border">
              <summary className="cursor-pointer px-4 py-3 text-sm font-semibold">
                {t(`dataAccessTheme.${themeCode}`, {
                  ns: NS,
                  defaultValue: themeCode,
                })}
              </summary>
              <div className="space-y-3 border-t border-border px-4 py-3">
                {themeDomains.map((domain) => {
                  const description = u3206ResolveDomainDescription(domain, i18n.language);
                  const allowed = allowedByCode.get(domain.code) ?? false;
                  return (
                    <div key={domain.code} className="rounded-md p-2 hover:bg-muted/40" title={description || undefined}>
                      <U2000CheckBox
                        formStateHandlers={domainCheckHandlers(domain.code, allowed)}
                        fieldName={domain.code}
                        label={u3206ResolveDomainLabel(domain, i18n.language)}
                        readonly={readonly}
                      />
                      {description ? (
                        <p className="mt-1 pl-8 text-xs text-muted-foreground">{description}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </details>
          ))}
        </div>

        {copySourceOptions.length > 0 && (
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-border p-4">
            <div className="min-w-[220px] flex-1">
              <U2000ComboBox<U3206CopySourceOption>
                formStateHandlers={copyFormHandlers as any}
                fieldName="copyDataAccessSource"
                label={t('copyDataAccessFrom', { ns: NS })}
                data={copySourceOptions}
                keyField="id"
                descField="label"
                readonly={readonly || copying}
                placeholder={t('copyDataAccessSelectPlaceholder', { ns: NS })}
                noneItemLabel={t('copyDataAccessSelectPlaceholder', { ns: NS })}
                getValue={(item) => item.id}
                findItemByValue={(value, items) => items.find((item) => item.id === value) ?? null}
                formatLabel={(item) => item.label}
              />
            </div>
            <U2000ActionBtn
              label={copying ? t('copyDataAccessInProgress', { ns: NS }) : t('copyDataAccessApply', { ns: NS })}
              disabled={readonly || copying || !copySourceId}
              handleClick={() => {
                void handleCopy();
              }}
            />
          </div>
        )}
      </div>

      <U3206DataAccessPreviewPanel
        allowedQuestions={preview.allowedQuestions}
        blockedQuestions={preview.blockedQuestions}
        t={t}
      />
    </div>
  );
}

export default U3206DataAccessTab;
